/** Gentle synthesized SFX — no scary stingers, no voice. */

let ctx: AudioContext | null = null;
let muted = false;

export function setMuted(m: boolean): void {
  muted = m;
}

function ac(): AudioContext | null {
  if (muted) return null;
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function env(g: GainNode, t: number, dur: number, peak: number): void {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.018);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
}

export function pluck(freq = 392, dur = 0.12): void {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = "triangle";
  o.frequency.setValueAtTime(freq, t);
  o.frequency.exponentialRampToValueAtTime(freq * 0.96, t + dur);
  env(g, t, dur, 0.08);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + dur + 0.02);
}

export function paperRustle(): void {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const n = c.createBuffer(1, c.sampleRate * 0.12, c.sampleRate);
  const d = n.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const src = c.createBufferSource();
  src.buffer = n;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1800;
  const g = c.createGain();
  g.gain.value = 0.04;
  src.connect(bp).connect(g).connect(c.destination);
  src.start(t);
}

export function correctChime(): void {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  [523.25, 659.25, 783.99].forEach((f, i) => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.value = f;
    env(g, t + i * 0.05, 0.28, 0.07);
    o.connect(g).connect(c.destination);
    o.start(t + i * 0.05);
    o.stop(t + 0.4);
  });
}

export function wrongThud(): void {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = "sine";
  o.frequency.setValueAtTime(140, t);
  o.frequency.exponentialRampToValueAtTime(70, t + 0.16);
  env(g, t, 0.18, 0.09);
  o.connect(g).connect(c.destination);
  o.start(t);
  o.stop(t + 0.2);
  // leaf shake
  const n = c.createBuffer(1, c.sampleRate * 0.14, c.sampleRate);
  const d = n.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const src = c.createBufferSource();
  src.buffer = n;
  const f = c.createBiquadFilter();
  f.type = "highpass";
  f.frequency.value = 1200;
  const gg = c.createGain();
  gg.gain.value = 0.03;
  src.connect(f).connect(gg).connect(c.destination);
  src.start(t);
}

export function moveSfx(): void {
  paperRustle();
  pluck(330, 0.1);
}

export function celebrate(): void {
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  [523, 659, 784, 1046].forEach((f, i) => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "triangle";
    o.frequency.value = f;
    env(g, t + i * 0.07, 0.32, 0.06);
    o.connect(g).connect(c.destination);
    o.start(t + i * 0.07);
    o.stop(t + 0.5);
  });
}

export function speak(text: string): void {
  if (muted) return;
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.92;
  u.pitch = 1.05;
  u.volume = 0.9;
  window.speechSynthesis.speak(u);
}
